import {isDefined} from "../../../libs/utils/epic.utils";

// Chrome's hints for the share picker, not yet in TypeScript's DOM types - other browsers ignore them
interface TabCaptureOptions extends DisplayMediaStreamOptions {
  preferCurrentTab: boolean;
  selfBrowserSurface: 'include' | 'exclude';
  surfaceSwitching: 'include' | 'exclude';
  monitorTypeSurfaces: 'include' | 'exclude';
}

export const SCREENSHOT_MAX_WIDTH: number = 1920;

const FOCUS_WAIT_MS: number = 3000;

export function isScreenshotSupported(): boolean {
  return isDefined(navigator.mediaDevices) && 'getDisplayMedia' in navigator.mediaDevices;
}

// The support needs the page, not the desktop: the picker offers this tab first and leaves whole screens out where the browser can
export async function captureTab(): Promise<Blob> {
  const options: TabCaptureOptions = {
    video: {displaySurface: 'browser'},
    audio: false,
    preferCurrentTab: true,
    selfBrowserSurface: 'include',
    surfaceSwitching: 'exclude',
    monitorTypeSurfaces: 'exclude'
  };
  const stream: MediaStream = await navigator.mediaDevices.getDisplayMedia(options);
  try {
    const video: HTMLVideoElement = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;
    await video.play();
    await waitForPage();
    return await toJpeg(video);
  } finally {
    stream.getTracks().forEach((t) => t.stop());
  }
}

// The picker takes the focus; wait until the page has it back and is painted again - bounded, a foreign window never gives it back
async function waitForPage(): Promise<void> {
  await new Promise<void>((resolve) => {
    const start: number = Date.now();
    const check = (): void => {
      if (document.hasFocus() || Date.now() - start >= FOCUS_WAIT_MS) {
        resolve();
      } else {
        setTimeout(check, 100);
      }
    };
    check();
  });
  await new Promise((r) => setTimeout(r, 300));
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
}

function toJpeg(video: HTMLVideoElement): Promise<Blob> {
  const scale: number = Math.min(1, SCREENSHOT_MAX_WIDTH / Math.max(1, video.videoWidth));
  const canvas: HTMLCanvasElement = document.createElement('canvas');
  canvas.width = Math.round(video.videoWidth * scale);
  canvas.height = Math.round(video.videoHeight * scale);
  canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) => {
    if (canvas.width === 0 || canvas.height === 0) {
      reject(new Error('Screenshot has no size'));
    } else {
      canvas.toBlob((b) => isDefined(b) ? resolve(b) : reject(new Error('Screenshot is empty')), 'image/jpeg', 0.8);
    }
  });
}
